import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { FileInfo } from '@/components/BatchUpload/type';
import baseConfig from '@/config';

let isCnArea = false;
if (typeof window !== 'undefined') {
  isCnArea = navigator.language === 'zh-CN';
}

export const DEFAULT_VOICE = {
  icon: '/images/voice/default_man_icon.png',
  previewUrl: 'https://assets.falcocut.ai/template/default_voiceInfo_man.mp3',
  title: isCnArea ? 'Christopher-美式英语' : 'Christopher-AmE',
  isPro: false,
  tags: isCnArea ? ['男', '年轻', '平静的'] : ['Male', 'Young Adult', 'Calm'],
  voiceId: 'Liam',
  // tabKey: '202407291529318462',
  voiceTemplateType: 1,
  isVcm: true,
};

export type SubtitleTranslationStoreProps = {
  clear: () => void;

  batchId: number;
  setBatchId: (value: number) => void;

  videoList: FileInfo[];
  setVideoList: (videoList: FileInfo[]) => void;

  selectedItem: any;
  setSelectedItem: (item: any) => void;

  language: {
    origin: string;
    target: string[];
  };
  setLanguage: (language: { origin: string; target: string[] }) => void;

  needTransCaption: boolean;
  setNeedTransCaption: (value: boolean) => void;

  needTransVoice: boolean;
  setNeedTransVoice: (value: boolean) => void;

  needLipSync: boolean;
  setNeedLipSync: (value: boolean) => void;

  subtitleType: number; //0-默认 1-翻译后字幕 2-双语字幕 3-无字幕
  setSubtitleType: (value: number) => void;

  fontTemplateInfo: {
    id: string;
    isPro: boolean;
  };
  setFontTemplateInfo: (value: { id: string; isPro: boolean }) => void;

  voiceInfo: {
    icon: string;
    previewUrl: string;
    title: string;
    isPro: false;
    tags: string[];
    voiceId: string;
    tabKey: string;
    voiceTemplateType: number;
    isVcm: boolean;
    state?: number;
    startTime?: number;
    systemTime?: number;
  };
  setVoiceInfo: (value: any) => void;

  submitDisabled: boolean;
  setSubmitDisabled: (value: boolean) => void;

  downloadAllDisable: boolean;
  setDownloadAllDisable: (value: boolean) => void;

  customSrtUrl?: string;
  setCustomSrtUrl: (value?: string) => void;

  speedMode: boolean;
  setSpeedMode: (value: boolean) => void;

  advancedSubtitleFileUrl: string;
  setAdvancedSubtitleFileUrl: (url: string) => void;

  advancedTerminologyFileUrl: string;
  setAdvancedTerminologyFileUrl: (url: string) => void;

  originalVoice: number;
  setOriginalVoice: (value: number) => void;
};

const getInitialState = () => {
  const storageVoiceInfo =
    typeof window !== 'undefined' ? localStorage.getItem('trans_voice_info') : null;
  const voiceInfo = storageVoiceInfo ? JSON.parse(storageVoiceInfo) : DEFAULT_VOICE;
  const needTransCaption =
    typeof window !== 'undefined'
      ? localStorage.getItem('trans_needTransCaption') !== 'false'
      : true;
  const needTransVoice =
    typeof window !== 'undefined'
      ? localStorage.getItem('trans_needTransVoice_v2') === 'true'
      : false;
  const needLipSync =
    typeof window !== 'undefined'
      ? localStorage.getItem('trans_needLipSync_v2') === 'true'
      : false;
  const subtitleType = needTransVoice && !needTransCaption ? 3 : needTransCaption ? 1 : 0;

  return {
    videoList: [],
    selectedItem: null,
    creditValue: 0,
    language: {
      origin: 'autoDetectLang',
      target:
        typeof window !== 'undefined'
          ? localStorage.getItem('trans_language_target')?.split(',') || []
          : [],
    },
    needTransCaption,
    needTransVoice,
    needLipSync,
    subtitleType,
    fontTemplateInfo: { id: '', isPro: false },
    voiceInfo,
    submitDisabled: true,
    downloadAllDisable: true,
    customSrtUrl: undefined,
    speedMode: true,

    advancedSubtitleFileUrl: '',
    advancedTerminologyFileUrl: '',
    originalVoice: 1,
  };
};

const useSubtitleTranslationStore = createWithEqualityFn<SubtitleTranslationStoreProps>()(
  devtools(
    (set, get) => ({
      batchId: 0,
      ...getInitialState(),

      clear: () => set(getInitialState()),

      setBatchId: (batchId) => set({ batchId }),

      setVideoList: (videoList) => set({ videoList }),

      setSelectedItem: (selectedItem) => set({ selectedItem }),

      setLanguage: (language) => set({ language }),

      setNeedTransCaption: (needTransCaption) => set({ needTransCaption }),

      setNeedTransVoice: (needTransVoice) => set({ needTransVoice }),

      setNeedLipSync: (needLipSync) => set({ needLipSync }),

      setSubtitleType: (subtitleType) => set({ subtitleType }),

      setFontTemplateInfo: (fontTemplateInfo) => set({ fontTemplateInfo }),

      setVoiceInfo: (voiceInfo) => set({ voiceInfo }),

      setSubmitDisabled: (submitDisabled) => set({ submitDisabled }),

      setDownloadAllDisable: (downloadAllDisable) => set({ downloadAllDisable }),

      setCustomSrtUrl: (customSrtUrl) => set({ customSrtUrl }),

      setSpeedMode: (speedMode) => set({ speedMode }),

      setAdvancedSubtitleFileUrl: (advancedSubtitleFileUrl) =>
        set({ advancedSubtitleFileUrl }),

      setAdvancedTerminologyFileUrl: (advancedTerminologyFileUrl) =>
        set({ advancedTerminologyFileUrl }),

      setOriginalVoice: (originalVoice) => set({ originalVoice }),
    }),
    {
      name: 'subtitleTraslation_store',
      enabled: baseConfig.debuggable,
    },
  ),
  shallow,
);

export default useSubtitleTranslationStore;
